import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { globalComplianceText } from "@/mocks/demo-flow";

export default function DemoTransitionPage() {
  const navigate = useNavigate();
  const [proceeding, setProceeding] = useState(false);

  const handleProceed = () => {
    setProceeding(true);
    setTimeout(() => {
      navigate("/inbound-alert");
    }, 700);
  };

  return (
    <div className="min-h-screen flex flex-col bg-background-50">
      {/* Minimalist Header */}
      <header className="h-16 lg:h-20 flex items-center px-4 md:px-6 lg:px-8 border-b border-background-100 bg-white flex-shrink-0">
        <Link to="/" className="flex items-center gap-2 cursor-pointer">
          <span className="w-7 h-7 lg:w-8 lg:h-8 flex items-center justify-center rounded-lg bg-primary-500 text-background-50">
            <i className="ri-heart-pulse-line text-base"></i>
          </span>
          <span className="font-heading text-sm lg:text-base font-bold text-foreground-950 whitespace-nowrap">
            Quick Prosthetics
          </span>
        </Link>
        <div className="ml-auto flex items-center gap-3">
          <span className="hidden sm:inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-foreground-100 text-[10px] font-semibold text-foreground-700">
            <span className="w-3 h-3 flex items-center justify-center">
              <i className="ri-lock-line text-[9px]"></i>
            </span>
            Secure Enterprise Portal
          </span>
        </div>
      </header>

      {/* Main Transition Slide */}
      <main className="flex-1 flex items-center justify-center px-4 md:px-6 py-10 md:py-16 relative overflow-hidden">
        {/* Subtle background texture */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[400px] rounded-full bg-background-200/30 blur-[120px]" />
          <div className="absolute bottom-0 right-0 w-[500px] h-[500px] rounded-full bg-background-100/40 blur-[100px]" />
        </div>

        <div className="relative z-10 w-full max-w-[800px] mx-auto space-y-8">
          {/* ===== MAIN HEADING ===== */}
          <div className="text-center space-y-4">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-foreground-200 bg-white text-[10px] md:text-[11px] font-semibold text-foreground-500 uppercase tracking-widest">
              <span className="w-1.5 h-1.5 rounded-full bg-accent-500 animate-pulse" />
              Investor Demo — Clinical Backend
            </div>
            <h1 className="font-heading text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-950 leading-tight max-w-2xl mx-auto">
              DEMO TRANSITION: ENTERPRISE CLINICAL BACKEND
            </h1>
          </div>

          {/* ===== CONTEXT BLOCK ===== */}
          <div className="bg-white rounded-2xl border border-background-200 p-6 md:p-8 space-y-4">
            <div className="flex items-start gap-3">
              <span className="w-6 h-6 flex items-center justify-center flex-shrink-0 text-foreground-500 mt-0.5">
                <i className="ri-information-line text-lg"></i>
              </span>
              <div className="space-y-3">
                <p className="text-sm md:text-base text-foreground-600 leading-relaxed">
                  The preceding screens represent the complete, patient-facing{" "}
                  <strong className="text-foreground-800">
                    "Digital Front Door"
                  </strong>{" "}
                  experience. Once the patient submits their HIPAA consent, a
                  secure, real-time notification email is instantly dispatched
                  to their chosen local clinic.
                </p>
                <p className="text-sm md:text-base text-foreground-600 leading-relaxed">
                  The following screens simulate the{" "}
                  <strong className="text-foreground-800">
                    enterprise clinical dashboard
                  </strong>
                  . In production, this administrative environment is strictly
                  hidden from patients and only accessible by platform
                  administrators, physicians, and verified clinic staff.
                </p>
              </div>
            </div>

            {/* Divider */}
            <div className="flex items-center gap-3 py-2">
              <div className="flex-1 h-px bg-background-200" />
              <span className="text-[10px] uppercase tracking-wider text-foreground-400 font-semibold">
                Perspective Shift
              </span>
              <div className="flex-1 h-px bg-background-200" />
            </div>

            {/* Patient/Clinic Visual Comparison */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="rounded-xl border border-background-200 bg-background-50 p-4 text-center">
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-accent-100 text-[10px] font-semibold text-accent-700 mb-3">
                  <span className="w-3 h-3 flex items-center justify-center">
                    <i className="ri-user-line text-[9px]"></i>
                  </span>
                  Patient Side
                </span>
                <p className="text-xs text-foreground-500 leading-relaxed">
                  Digital Front Door
                  <br />
                  Intake &amp; Consent
                  <br />
                  Clinic Selection
                </p>
              </div>
              <div className="rounded-xl border-2 border-foreground-300 bg-background-100 p-4 text-center relative">
                <span className="absolute -top-2.5 left-1/2 -translate-x-1/2 inline-flex items-center px-2 py-0.5 rounded-full bg-foreground-900 text-white text-[8px] font-bold uppercase tracking-wider whitespace-nowrap">
                  Now Viewing
                </span>
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-foreground-100 text-[10px] font-semibold text-foreground-700 mb-3 mt-1">
                  <span className="w-3 h-3 flex items-center justify-center">
                    <i className="ri-building-2-line text-[9px]"></i>
                  </span>
                  Clinic Side
                </span>
                <p className="text-xs text-foreground-600 leading-relaxed font-medium">
                  AI Artifacts Review
                  <br />
                  ROI Dashboard
                  <br />
                  Patient File Unlock
                </p>
              </div>
            </div>
          </div>

          {/* ===== PRIMARY CTA ===== */}
          <div className="text-center">
            {!proceeding ? (
              <button
                type="button"
                onClick={handleProceed}
                className="inline-flex items-center gap-3 px-10 py-5 rounded-xl bg-foreground-900 text-background-50 text-sm md:text-base font-bold hover:bg-foreground-800 transition-all cursor-pointer whitespace-nowrap"
              >
                <span className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-eye-line text-base"></i>
                </span>
                Let&apos;s See the Backend
                <span className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-arrow-right-line text-base"></i>
                </span>
              </button>
            ) : (
              <div className="inline-flex items-center gap-3 px-10 py-5 rounded-xl bg-accent-50 border border-accent-200 animate-fadeSlideIn">
                <span className="w-5 h-5 flex items-center justify-center text-accent-600">
                  <i className="ri-loader-4-line text-base animate-spin"></i>
                </span>
                <span className="text-sm font-semibold text-accent-700">
                  Loading Backend View...
                </span>
              </div>
            )}
          </div>
        </div>
      </main>

      {/* Compliance Footer */}
      <footer className="w-full bg-background-100 border-t border-background-200 mt-auto">
        <div className="max-w-6xl mx-auto px-4 md:px-6 py-4">
          <div className="flex items-start gap-2.5">
            <span className="w-4 h-4 flex items-center justify-center flex-shrink-0 text-foreground-500 mt-0.5">
              <i className="ri-information-line text-[11px]"></i>
            </span>
            <p className="text-[10px] md:text-[11px] text-foreground-500 leading-relaxed">
              {globalComplianceText}
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
